import * as Clipboard from "expo-clipboard";
import { useTheme } from "expo-router/react-navigation";
import { ComponentRef, FC, useMemo, useRef, useState } from "react";
import { Platform, View } from "react-native";

import { BarChart, GroupedBarChart } from "@/components/charts";
import { Card, Center, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
import ChartCopyButton from "./ChartCopyButton";
import { copyChartToClipboard } from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface RatingScaleQuestionResultsProps {
  compact?: boolean;
  result: QuestionResults;
  selectedGroupIndex: "all" | string;
}

/* ── Helpers ────────────────────────────────────────────── */

const PURPLE = "#7c3aed";

function StarRow({ average, max }: { average: number; max: number }) {
  return (
    <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
      {Array.from({ length: max }, (_, i) => {
        const pct = Math.round(Math.min(1, Math.max(0, average - i)) * 100);
        const gid = `rs-bis-${i}`;
        return (
          <svg height="36" key={i} viewBox="0 0 24 24" width="36">
            <defs>
              <linearGradient id={gid} x1="0" x2="1" y1="0" y2="0">
                <stop offset={`${pct}%`} stopColor={PURPLE} />
                <stop offset={`${pct}%`} stopColor="#ede9fe" />
              </linearGradient>
            </defs>
            <path
              d="M12 2l2.83 8.7H23l-7.38 5.36 2.83 8.71L12 19.4l-6.45 5.37 2.83-8.71L2 10.7h8.17z"
              fill={`url(#${gid})`}
            />
          </svg>
        );
      })}
    </div>
  );
}

/* ── Component ──────────────────────────────────────────── */

const RatingScaleQuestionResults: FC<RatingScaleQuestionResultsProps> = ({
  compact = false,
  result,
  selectedGroupIndex,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const { groups, question } = result;
  const isSingleGroup = groups.length === 1;

  const ratingRef = useRef<ComponentRef<typeof View>>(null);
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [ratingCopied, setRatingCopied] = useState(false);
  const [chartCopied, setChartCopied] = useState(false);

  const scaleSize =
    question.options.length >= 3 && question.options.length <= 10
      ? question.options.length
      : 5;
  const scaleLabels = useMemo(
    () =>
      question.options.map((opt: string) =>
        i18n.t("questions.stars", { count: parseInt(opt, 10) })
      ),
    [i18n, question.options]
  );

  const totalRespondents = useMemo(
    () =>
      groups.reduce(
        (sum, g) =>
          sum +
          g.answers.filter(
            ({ answer }) =>
              answer.type === "rating_scale" && answer.rating !== null
          ).length,
        0
      ),
    [groups]
  );

  const averageRating = useMemo(() => {
    let sum = 0;
    let count = 0;
    groups.forEach((g) =>
      g.answers.forEach(({ answer }) => {
        if (answer.type === "rating_scale" && answer.rating !== null) {
          sum += answer.rating;
          count++;
        }
      })
    );
    return count > 0 ? sum / count : null;
  }, [groups]);

  const barChartPoints = useMemo(() => {
    if (!isSingleGroup) return [];
    const counts: number[] = scaleLabels.map(() => 0);
    groups[0].answers.forEach(({ answer }) => {
      if (answer.type === "rating_scale" && answer.rating !== null) {
        const idx = answer.rating - 1;
        if (idx >= 0 && idx < counts.length) counts[idx]++;
      }
    });
    return scaleLabels.map((label: string, idx: number) => ({
      x: label,
      y: counts[idx],
    }));
  }, [groups, isSingleGroup, scaleLabels]);

  const barChartGroups = useMemo(() => {
    if (isSingleGroup) return [];
    return groups.map((group, groupIdx) => {
      const counts: number[] = scaleLabels.map(() => 0);
      group.answers.forEach(({ answer }) => {
        if (answer.type === "rating_scale" && answer.rating !== null) {
          const idx = answer.rating - 1;
          if (idx >= 0 && idx < counts.length) counts[idx]++;
        }
      });
      return {
        key: group.group.id || `group-${groupIdx}`,
        label:
          group.group.name ||
          group.group.panel?.name ||
          (i18n.t("studies.group", { index: groupIdx + 1 }) as string),
        points: scaleLabels.map((label: string, idx: number) => ({
          x: label,
          y: counts[idx],
        })),
        respondentCount: group.answers.filter(
          ({ answer }) =>
            answer.type === "rating_scale" && answer.rating !== null
        ).length,
      };
    });
  }, [groups, i18n, isSingleGroup, scaleLabels]);

  const copyRating = async () => {
    const element = ratingRef.current as unknown as HTMLElement;
    if (!element) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(element, {
        backgroundColor: "#ffffff",
        scale: 2,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = (reader.result as string).split(",")[1];
          await Clipboard.setImageAsync(base64);
          setRatingCopied(true);
          setTimeout(() => setRatingCopied(false), 2000);
        };
        reader.readAsDataURL(blob);
      }, "image/png");
    } catch (error) {
      console.error("Failed to copy rating:", error);
    }
  };

  const copyChart = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    await copyChartToClipboard(element, () => {
      setChartCopied(true);
      setTimeout(() => setChartCopied(false), 2000);
    });
  };

  return (
    <HStack className="gap-3 items-stretch flex-col lg:flex-row">
      {/* Card 1 — Average rating */}
      <Card
        className={`group rounded-2xl w-full min-w-0 lg:flex-1 ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 p-6"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={ratingCopied}
          onPress={copyRating}
          textColor={theme.colors.text}
        />
        <VStack className="gap-4 flex-1">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("dashboardNavigator.chartTitleRatings", {
              count: totalRespondents,
            })}
          </Text>
          <View
            ref={ratingRef}
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              width: "100%",
            }}
          >
            {averageRating === null ? (
              <Center>
                <Text className="text-typography-500">
                  {i18n.t("dashboardNavigator.noData")}
                </Text>
              </Center>
            ) : Platform.OS === "web" ? (
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  padding: "24px 0",
                }}
              >
                <div
                  style={{
                    color: PURPLE,
                    fontSize: 64,
                    fontWeight: 700,
                    lineHeight: 1.1,
                  }}
                >
                  {averageRating.toFixed(1)}
                </div>
                <StarRow average={averageRating} max={scaleSize} />
                <div
                  style={{
                    color: "hsl(220,10%,46%)",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  {i18n.t("questions.outOfStars", { count: scaleSize })}
                </div>
              </div>
            ) : (
              <VStack className="items-center gap-2 py-6">
                <Text className="text-5xl font-bold" style={{ color: PURPLE }}>
                  {averageRating.toFixed(1)}
                </Text>
                <Text className="text-sm text-typography-500">
                  {i18n.t("questions.outOfStars", { count: scaleSize })}
                </Text>
              </VStack>
            )}
          </View>
        </VStack>
      </Card>

      {/* Card 2 — Rating distribution */}
      <Card
        className={`group rounded-2xl w-full min-w-0 lg:flex-1 ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 px-6 pt-6 pb-2"}`}
      >
        <ChartCopyButton
          compact={compact}
          copied={chartCopied}
          onPress={copyChart}
          textColor={theme.colors.text}
        />
        <VStack className="gap-6 flex-1">
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
            {i18n.t("dashboardNavigator.chartTitleRatingsBreakdown", {
              count: totalRespondents,
            })}
          </Text>
          <View
            ref={chartRef}
            style={{
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              width: "100%",
            }}
          >
            {isSingleGroup ? (
              <BarChart
                minWidth={selectedGroupIndex === "all" ? 600 : 400}
                points={barChartPoints}
                totalRespondents={totalRespondents}
              />
            ) : (
              <GroupedBarChart
                groups={barChartGroups}
                minWidth={selectedGroupIndex === "all" ? 600 : 400}
              />
            )}
          </View>
        </VStack>
      </Card>
    </HStack>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default RatingScaleQuestionResults;
